import * as React from 'react';
import { useContext, useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { DEFAULT_QUERY_PROMPTS_FETCH_LIMIT } from 'bundles/search-common/components/experiments/aiSearchOnLohp/constants';
import type { SearchQueryPromptRouteType } from 'bundles/search-common/providers/searchTypes';
import RECOMMEND_QUERY_PROMPTS from 'bundles/search-common/queries/recommendQueryPrompts.graphql';

export type QueryPrompt = {
  id: string;
  promptTextLong: string;
  promptTextShort: string;
  routeType: SearchQueryPromptRouteType;
  routeParameter: string;
  intentType?: string;
};

type RecommendQueryPromptsResponse = {
  SearchResult: {
    recommendQueryPrompts: {
      elements: QueryPrompt[];
      pagination: {
        totalElements: number | null;
        cursor: string | null;
      };
    };
  };
};

type RecommendQueryPromptsVariables = {
  query?: string;
  limit: number;
  cursor?: string;
};

type QueryPromptsProviderProps = {
  /** The search query to get prompts for. Omit for LOHP (homepage) prompts. */
  query?: string;
  /** Maximum number of prompts to return. Defaults to 10. */
  limit?: number;
  /** Skip fetching prompts. Defaults to false. */
  skip?: boolean;
  children: React.ReactNode;
};

type QueryPromptsContextValue = {
  prompts: QueryPrompt[] | undefined;
  loading: boolean;
  error: Error | undefined;
  pagination: { totalElements: number | null; cursor: string | null } | undefined;
};

const EMPTY_PROMPTS_CONTEXT: QueryPromptsContextValue = {
  prompts: undefined,
  loading: false,
  error: undefined,
  pagination: undefined,
};

// Default is a safe empty state so components using useQueryPrompts() work correctly
// even when rendered outside QueryPromptsProvider (e.g. control-group users who never
// see the provider in the tree).
const QueryPromptsContext = React.createContext<QueryPromptsContextValue>(EMPTY_PROMPTS_CONTEXT);

export const useQueryPrompts = (): QueryPromptsContextValue => {
  return useContext(QueryPromptsContext);
};

const QueryPromptsProvider = ({
  query,
  limit = DEFAULT_QUERY_PROMPTS_FETCH_LIMIT,
  skip = false,
  children,
}: QueryPromptsProviderProps) => {
  const { loading, error, data } = useQuery<RecommendQueryPromptsResponse, RecommendQueryPromptsVariables>(
    RECOMMEND_QUERY_PROMPTS,
    {
      variables: {
        query,
        limit,
      },
      // Experiment gating is the parent's responsibility (conditional rendering in
      // SearchBarContentWrapper). This provider only needs its own skip prop.
      skip,
      fetchPolicy: 'cache-first',
    }
  );

  const promptsData = data?.SearchResult?.recommendQueryPrompts;

  const contextValue = useMemo<QueryPromptsContextValue>(
    () => ({
      prompts: promptsData?.elements,
      loading,
      error,
      pagination: promptsData?.pagination,
    }),
    [promptsData, loading, error]
  );

  return <QueryPromptsContext.Provider value={contextValue}>{children}</QueryPromptsContext.Provider>;
};

export default QueryPromptsProvider;
